import { useEffect, useRef, useCallback } from 'react'

const STORAGE_KEY = 'budget_last_activity'
const TIMEOUT_MS = 30 * 60 * 1000        // 30 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000  // avertissement 2 min avant

/**
 * Détecte l'inactivité utilisateur et déclenche la déconnexion automatique.
 * Fonctionne sur Web, PWA Android / iPhone / Desktop.
 *
 * Sur mobile (PWA), le timer JS peut être suspendu par l'OS. On utilise
 * localStorage pour stocker le timestamp de la dernière activité et le
 * vérifier au retour de l'app (visibilitychange).
 *
 * @param {object} options
 * @param {function} options.onLogout   - appelé quand le délai est dépassé
 * @param {function} options.onWarning  - appelé 2 min avant la déconnexion
 */
export function useInactivityLogout({ onLogout, onWarning }) {
  const logoutTimerRef  = useRef(null)
  const warningTimerRef = useRef(null)
  const warningFiredRef = useRef(false)

  const clearTimers = useCallback(() => {
    clearTimeout(logoutTimerRef.current)
    clearTimeout(warningTimerRef.current)
  }, [])

  const resetTimer = useCallback(() => {
    clearTimers()
    warningFiredRef.current = false
    localStorage.setItem(STORAGE_KEY, String(Date.now()))

    warningTimerRef.current = setTimeout(() => {
      warningFiredRef.current = true
      onWarning?.()
    }, TIMEOUT_MS - WARNING_BEFORE_MS)

    logoutTimerRef.current = setTimeout(() => {
      onLogout?.()
    }, TIMEOUT_MS)
  }, [clearTimers, onLogout, onWarning])

  // Vérifie l'inactivité au retour au premier plan (important pour PWA mobile)
  const checkOnResume = useCallback(() => {
    const last = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10)
    if (!last) return
    const elapsed = Date.now() - last
    if (elapsed >= TIMEOUT_MS) {
      clearTimers()
      onLogout?.()
    } else if (elapsed >= TIMEOUT_MS - WARNING_BEFORE_MS && !warningFiredRef.current) {
      warningFiredRef.current = true
      onWarning?.()
      // Replanifie le logout pour le temps restant
      clearTimeout(logoutTimerRef.current)
      logoutTimerRef.current = setTimeout(() => onLogout?.(), TIMEOUT_MS - elapsed)
    }
  }, [clearTimers, onLogout, onWarning])

  useEffect(() => {
    const EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'touchmove', 'click', 'scroll']

    const handleActivity = () => resetTimer()
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkOnResume()
    }

    EVENTS.forEach(ev => window.addEventListener(ev, handleActivity, { passive: true }))
    document.addEventListener('visibilitychange', handleVisibility)

    resetTimer() // démarre le timer au montage

    return () => {
      EVENTS.forEach(ev => window.removeEventListener(ev, handleActivity))
      document.removeEventListener('visibilitychange', handleVisibility)
      clearTimers()
    }
  }, [resetTimer, checkOnResume, clearTimers])
}
